import type { RequestConfig } from "@app/config/axios/http";
import { boApi, type ApiOmpBoState } from "@src/api/bo";
import { divisionApi, divisionServices, type ApiDivision } from "@src/api/dictionaries/divisions";
import { measureApi, type ApiMeasure } from "@src/api/dictionaries/measures";
import type { PaginationReturns } from "@src/api/types/PaginationReturns";
import { stockObjsServices } from "@src/api/stock/stockObjs";
import type { StockObj } from "@src/api/stock/stockObjs";
import { limitCardsApi } from "./api.core";
import type {
    ApiLeaveLimitCard,
    ApiLimitCard,
    ApiLimitCardContent,
    LeaveLimitCard,
    LeaveLimitCardExtends,
    LeaveLimitCardFilter,
    LimitCard,
    LimitCardContent,
    LimitCardContentsExtends,
    LimitCardContentsFilter,
    LimitCardExtends,
    LimitCardFilter,
} from "./types";

/**
 * Сервисный слой — лимитные карты: агрегация заголовка, состава и отпусков с обогащением связей.
 */
export const limitCardsServices = {
    ...limitCardsApi,

    /**
     * Загружает заголовки ЛК и при необходимости обогащает их связанными сущностями.
     * @param params.flt фильтр {@link LimitCardFilter}
     * @param params.include набор связей для догрузки
     * @param params.includeBo вложенные связи БО
     * @param params.offset смещение
     * @param params.limit лимит
     * @param config опции HTTP
     */
    load: async (
        params: {
            flt?: LimitCardFilter;
            include?: Array<keyof LimitCardExtends>;
            includeBo?: Array<"State">;
            offset?: number;
            limit?: number;
        },
        config?: RequestConfig,
    ): Promise<PaginationReturns<LimitCard>> => {
        const { flt, include, includeBo = [], offset, limit } = params;

        const { items: limitCards, totalQtyItems } = await limitCardsApi.load({ flt, offset, limit }, config);

        if (include == null) {
            return {
                items: limitCards.map((cards) => serializeLimitCard(cards)),
                totalQtyItems,
            };
        }

        const divisionCodes = new Set<number>();
        const sectionCodes = new Set<number>();
        const boStateCodes = new Set<number>();

        const includeSender = include.includes("Sender");
        const includeReceiver = include.includes("Receiver");
        const includeSenderSection = include.includes("SenderSection");
        const includeReceiverSection = include.includes("ReceiverSection");
        const includeBoData = include.includes("Bo");
        const includeBoState = includeBoData && includeBo?.includes("State");

        limitCards.forEach((cards) => {
            if (includeSender) divisionCodes.add(cards.SenderCode);
            if (includeReceiver) divisionCodes.add(cards.ReceiverCode);
            if (includeReceiverSection && cards.ReceiverSectionCode !== null) {
                sectionCodes.add(cards.ReceiverSectionCode);
            }
            if (includeBoState) boStateCodes.add(cards.Bo.State);
        });

        const divisions = new Map<number, ApiDivision>();
        const sections = new Map<number, ApiDivision>();
        const boStates = new Map<number, ApiOmpBoState>();

        const promises: Promise<unknown>[] = [];

        if ((includeSender || includeReceiver) && divisionCodes.size > 0) {
            const loadedDivisions = divisionApi.load({ flt: { Codes: [...divisionCodes] } }, config);
            void loadedDivisions.then(({ items }) => items.forEach((div) => divisions.set(div.Code, div)));
            promises.push(loadedDivisions);
        }

        if ((includeSenderSection || includeReceiverSection) && sectionCodes.size > 0) {
            const loadedSections = divisionServices.loadBySectionCodes({ sectionCodes: [...sectionCodes] }, config);
            void loadedSections.then((items) =>
                items.forEach((item) => {
                    if (item.SectionCode !== 0) {
                        sections.set(item.SectionCode, item);
                    }
                }),
            );
            promises.push(loadedSections);
        }

        if (includeBoState && boStateCodes.size > 0) {
            const loadedBoStates = boApi.states.load({ Codes: [...boStateCodes] }, config);
            void loadedBoStates.then((states) => states.forEach((state) => boStates.set(state.Code, state)));
            promises.push(loadedBoStates);
        }

        await Promise.all(promises);

        const items = limitCards.map<LimitCard>((card) =>
            serializeLimitCard(card, {
                Sender: includeSender ? divisions.get(card.SenderCode) : undefined,
                Receiver: includeReceiver ? divisions.get(card.ReceiverCode) : undefined,
                ReceiverSection:
                    includeReceiverSection && card.ReceiverSectionCode !== null
                        ? (sections.get(card.ReceiverSectionCode) ?? null)
                        : undefined,
                Bo: {
                    StateCode: card.Bo.State,
                    State: includeBoState ? boStates.get(card.Bo.State) : undefined,
                },
            }),
        );

        return { items, totalQtyItems };
    },

    /**
     * Возвращает `LimitCardCode` по коду отпуска (цепочка leave → content → header).
     * @param leaveLimitCode код отпуска по ЛК
     * @returns код заголовка ЛК или `null` / `undefined`, если цепочка не найдена
     * @throws Error при сбое загрузки промежуточных данных
     */
    loadLimitCardCodeByLeaveCode: async (leaveLimitCode: number): Promise<number | null | undefined> => {
        try {
            const { items: leaveLimitCards } = await limitCardsApi.leave.load({ flt: { Codes: [leaveLimitCode] } });

            if (leaveLimitCards == null) {
                throw new Error(`Отпуск с soCode=${leaveLimitCode} не найден`);
            }

            const leaveLimitCard = leaveLimitCards[0];
            const { items: limitCardContents } = await limitCardsApi.content.load({
                flt: { Codes: [leaveLimitCard.LimitCardContentCode] },
            });

            if (limitCardContents == null) {
                throw new Error(
                    `Содержимое лимитной карты с LimitCardContentCode=${leaveLimitCard.LimitCardContentCode} не найдено`,
                );
            }
            const limitCardContent = limitCardContents[0];

            return limitCardContent?.LimitCardCode;
        } catch {
            throw new Error("Ошибка при получении кода лимитной карты");
        }
    },

    content: {
        ...limitCardsApi.content,

        /**
         * Загружает состав ЛК и обогащает строки связанными ТМЦ и единицами измерения.
         * @param params.flt фильтр состава
         * @param params.include набор связей для догрузки
         * @param params.offset смещение
         * @param params.limit лимит
         */
        load: async (params: {
            flt?: LimitCardContentsFilter;
            include?: Array<keyof LimitCardContentsExtends>;
        }): Promise<PaginationReturns<LimitCardContent>> => {
            const { flt, include } = params;

            const { items: limitCardContents, totalQtyItems } = await limitCardsApi.content.load({ flt });

            if (include == null) {
                return {
                    items: limitCardContents.map((cards) => serializeLimitCardContent(cards)),
                    totalQtyItems,
                };
            }

            const stockObjsCodes = new Set<number>();
            const measuresCodes = new Set<number>();

            const includeStockObj = include.includes("StockObj");
            const includeMainMeas = include.includes("MeasMain");
            const includeAddMeas = include.includes("MeasAdd");

            limitCardContents.forEach((cards) => {
                if (includeStockObj) stockObjsCodes.add(cards.StockobjCode);
                if (includeMainMeas) measuresCodes.add(cards.MeasMainCode);
                if (includeAddMeas) measuresCodes.add(cards.MeasAddCode);
            });

            const stockObjs = new Map<number, StockObj>();
            const measures = new Map<number, ApiMeasure>();

            const promises: Promise<unknown>[] = [];

            if (includeStockObj && stockObjsCodes.size > 0) {
                const loadedStockObjs = stockObjsServices.loadByFilter({ flt: { Codes: [...stockObjsCodes] } });
                void loadedStockObjs.then((objs) => objs.forEach((obj) => stockObjs.set(obj.Code, obj)));
                promises.push(loadedStockObjs);
            }

            if ((includeMainMeas || includeAddMeas) && measuresCodes.size > 0) {
                const loadedMeasures = measureApi.load({ flt: { Codes: [...measuresCodes] } });
                void loadedMeasures.then((meas) => meas.forEach((mea) => measures.set(mea.Code, mea)));
                promises.push(loadedMeasures);
            }

            await Promise.all(promises);

            const items = limitCardContents.map<LimitCardContent>((card) =>
                serializeLimitCardContent(card, {
                    StockObj: includeStockObj ? stockObjs.get(card.StockobjCode) : undefined,
                    MeasMain: includeMainMeas ? measures.get(card.MeasMainCode) : undefined,
                    MeasAdd: includeAddMeas ? measures.get(card.MeasAddCode) : undefined,
                }),
            );

            return { items, totalQtyItems };
        },
    },

    leave: {
        ...limitCardsApi.leave,

        /**
         * Загружает отпуска по ЛК с опциональной догрузкой состава и состояния БО.
         * @param params.flt фильтр отпусков
         * @param params.include набор связей для догрузки
         * @param params.includeBo вложенные связи БО
         * @param params.includeLimitContent связи строки состава
         * @param params.offset смещение
         * @param params.limit лимит
         */
        load: async (params: {
            flt?: LeaveLimitCardFilter;
            include?: Array<keyof LeaveLimitCardExtends>;
            includeBo?: Array<"State">;
            includeLimitContent?: Array<keyof LimitCardContentsExtends>;
            offset?: number;
            limit?: number;
        }): Promise<PaginationReturns<LeaveLimitCard>> => {
            const { flt, include = [], includeBo = [], includeLimitContent, offset, limit } = params;

            const { items: leaveCards, totalQtyItems } = await limitCardsApi.leave.load({ flt, offset, limit });

            const includeBoState = include.includes("Bo") && includeBo.includes("State");
            const _includeLimitContent = include.includes("LimitCardContent");

            const boStateCodes = new Set<number>();
            const limitCardContentCodes = new Set<number>();

            leaveCards.forEach((card) => {
                if (includeBoState) {
                    boStateCodes.add(card.Bo.State);
                }
                if (_includeLimitContent) {
                    limitCardContentCodes.add(card.LimitCardContentCode);
                }
            });

            const boStatesMap = new Map<number, ApiOmpBoState>();
            const contentMap = new Map<number, LimitCardContent>();

            const promises: Promise<unknown>[] = [];

            if (includeBoState && boStateCodes.size > 0) {
                const boStateLoad = boApi.states.load({ Codes: [...boStateCodes] });
                void boStateLoad.then((states) => states.forEach((state) => boStatesMap.set(state.Code, state)));
                promises.push(boStateLoad);
            }

            if (_includeLimitContent && limitCardContentCodes.size > 0) {
                const contentLoad = limitCardsServices.content.load({
                    flt: { Codes: [...limitCardContentCodes] },
                    include: includeLimitContent,
                });
                void contentLoad.then((contents) => {
                    contents.items.forEach((content) => contentMap.set(content.Code, content));
                });
                promises.push(contentLoad);
            }

            await Promise.all(promises);

            const items = leaveCards.map<LeaveLimitCard>((card) =>
                serializeLeaveLimitCard(card, {
                    Bo: {
                        StateCode: card.Bo.State,
                        State: includeBoState ? boStatesMap.get(card.Bo.State) : undefined,
                    },
                    LimitCardContent: _includeLimitContent ? contentMap.get(card.LimitCardContentCode) : undefined,
                }),
            );

            return { items, totalQtyItems };
        },
    },
} as const;

function serializeLimitCard(apiLimitCard: ApiLimitCard, includeData?: Partial<LimitCardExtends>): LimitCard {
    return {
        Code: apiLimitCard.Code,
        Num: apiLimitCard.Num,
        StartDate: apiLimitCard.StartDate,
        FinishDate: apiLimitCard.FinishDate,
        TypeCode: apiLimitCard.TypeCode,
        DocDate: apiLimitCard.DocDate,
        SenderCode: apiLimitCard.SenderCode,
        Sender: includeData?.Sender,
        SenderSection: includeData?.SenderSection,
        ReceiverCode: apiLimitCard.ReceiverCode,
        Receiver: includeData?.Receiver,
        ReceiverSectionCode: apiLimitCard.ReceiverSectionCode,
        ReceiverSection: includeData?.ReceiverSection,
        Notice: apiLimitCard.Notice,
        Bo: includeData?.Bo,
    };
}

function serializeLimitCardContent(
    apiLimitCard: ApiLimitCardContent,
    includeData?: Partial<LimitCardContentsExtends>,
): LimitCardContent {
    return {
        ...apiLimitCard,
        StockObj: includeData?.StockObj,
        MeasMain: includeData?.MeasMain,
        MeasAdd: includeData?.MeasAdd,
    };
}

function serializeLeaveLimitCard(
    apiLeaveCard: ApiLeaveLimitCard,
    includeData?: Partial<LeaveLimitCardExtends>,
): LeaveLimitCard {
    return {
        ...apiLeaveCard,
        LimitCardContent: includeData?.LimitCardContent,
        Bo: includeData?.Bo,
    };
}
